import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { fetchAnalyticsData } from '../services/analyticsService';
import { 
  Map,
  Download,
  Filter,
  Menu,
  X,
  BarChart,
  File,
  Users,
  Settings,
  ZoomIn,
  ZoomOut,
  RotateCcw
} from 'lucide-react';
import { MapContainer, TileLayer, Circle, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ResponsiveSidebar from '../components/layout/ResponsiveSidebar';

// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface LocationData {
  lat: number;
  lng: number;
  count: number;
  category: string;
  description?: string;
}

const mockLocationData: LocationData[] = [
  { lat: 28.6139, lng: 77.2090, count: 45, category: 'roads', description: 'Potholes and road damage near Connaught Place, New Delhi' },
  { lat: 28.6129, lng: 77.2295, count: 32, category: 'garbage', description: 'Garbage collection delay in Central Delhi' },
  { lat: 28.5355, lng: 77.3910, count: 28, category: 'streetlight', description: 'Street light outages in Noida sector 62' },
  { lat: 28.4595, lng: 77.0266, count: 21, category: 'water', description: 'Water supply issues in Gurgaon' },
  { lat: 28.7041, lng: 77.1025, count: 19, category: 'electricity', description: 'Power cuts in North Delhi' },
  { lat: 28.6691, lng: 77.4538, count: 35, category: 'roads', description: 'Road construction delays in East Delhi' },
  { lat: 28.5245, lng: 77.1855, count: 26, category: 'garbage', description: 'Overflowing garbage bins in South Delhi' },
  { lat: 28.6304, lng: 77.2177, count: 23, category: 'streetlight', description: 'Broken street lights in Old Delhi area' },
  { lat: 19.0760, lng: 72.8777, count: 52, category: 'water', description: 'Water leakage in Mumbai Central' },
  { lat: 19.2183, lng: 72.9781, count: 38, category: 'electricity', description: 'Frequent power outages in Thane' },
  { lat: 19.1706, lng: 72.8301, count: 41, category: 'roads', description: 'Traffic congestion on Linking Road, Bandra' },
  { lat: 19.0330, lng: 73.0297, count: 29, category: 'garbage', description: 'Waste management issues in Navi Mumbai' },
  { lat: 12.9716, lng: 77.5946, count: 47, category: 'roads', description: 'Road repair needed on MG Road, Bangalore' },
  { lat: 12.9141, lng: 77.5955, count: 33, category: 'water', description: 'Irregular water supply in JP Nagar' },
  { lat: 12.9784, lng: 77.6343, count: 26, category: 'electricity', description: 'Power fluctuations in Koramangala' },
  { lat: 13.0018, lng: 77.5726, count: 22, category: 'garbage', description: 'Garbage collection delay in Whitefield' },
  { lat: 13.0827, lng: 80.2707, count: 39, category: 'roads', description: 'Pothole repairs needed in Chennai city center' },
  { lat: 13.0413, lng: 80.2337, count: 31, category: 'water', description: 'Water quality issues in T Nagar' },
  { lat: 13.1171, lng: 80.1952, count: 25, category: 'electricity', description: 'Transformer issues in Guindy' },
  { lat: 13.0072, lng: 80.2574, count: 28, category: 'streetlight', description: 'Street light maintenance in Velachery' },
  { lat: 17.3850, lng: 78.4867, count: 42, category: 'roads', description: 'Road widening project delays in Hyderabad' },
  { lat: 17.4483, lng: 78.3915, count: 35, category: 'garbage', description: 'Waste segregation issues in Hitech City' },
  { lat: 17.3616, lng: 78.4747, count: 27, category: 'water', description: 'Water pressure issues in Gachibowli' },
  { lat: 17.4931, lng: 78.3915, count: 24, category: 'electricity', description: 'Power supply issues in Kondapur' },
  { lat: 22.5726, lng: 88.3639, count: 48, category: 'roads', description: 'Traffic signal malfunction in Kolkata CBD' },
  { lat: 22.5677, lng: 88.4204, count: 36, category: 'water', description: 'Water pipe burst in Salt Lake City' },
  { lat: 22.5411, lng: 88.3372, count: 29, category: 'electricity', description: 'Voltage fluctuations in South Kolkata' },
  { lat: 22.6341, lng: 88.4149, count: 23, category: 'garbage', description: 'Waste disposal issues in Rajarhat' }
];

const MapView: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useLanguage();
  const [selectedTimeRange, setSelectedTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [locationData, setLocationData] = useState<LocationData[]>(mockLocationData);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mapRef = useRef<L.Map | null>(null);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const analyticsData = await fetchAnalyticsData(selectedTimeRange);
        if (analyticsData) {
          setLocationData(analyticsData.locationData.map(item => ({
            lat: item.lat,
            lng: item.lng,
            count: item.count,
            category: item.category,
            description: undefined
          })));
        } else {
          setLocationData(mockLocationData);
        }
      } catch (error) {
        console.error('Error loading map data:', error);
        setLocationData(mockLocationData);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedTimeRange]);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      roads: '#EF4444',
      garbage: '#F59E0B',
      streetlight: '#3B82F6',
      water: '#06B6D4',
      electricity: '#8B5CF6'
    };
    return colors[category] || '#6B7280';
  };

  const exportMapData = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    
    csvContent += "Latitude,Longitude,Complaints,Category\n";
    
    locationData.forEach(item => {
      csvContent += `${item.lat},${item.lng},${item.count},${t(`categories.${item.category}`) || item.category}\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "map_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };

  const handleResetView = () => {
    if (mapRef.current) {
      const centerLat = locationData.length > 0 ? locationData.reduce((sum, point) => sum + point.lat, 0) / locationData.length : 28.6139;
      const centerLng = locationData.length > 0 ? locationData.reduce((sum, point) => sum + point.lng, 0) / locationData.length : 77.2090;
      mapRef.current.setView([centerLat, centerLng], 5);
    }
  };

  const GeographicMap: React.FC<{ data: LocationData[] }> = ({ data }) => {
    const centerLat = data.length > 0 ? data.reduce((sum, point) => sum + point.lat, 0) / data.length : 0;
    const centerLng = data.length > 0 ? data.reduce((sum, point) => sum + point.lng, 0) / data.length : 0;
    
    console.log('Map data:', data);
    console.log('Data length:', data.length);
    console.log('Map center:', centerLat, centerLng);
    
    if (data && data.length > 0) {
      console.log('First 3 data points:', data.slice(0, 3));
    }
    
    const handleMapLoad = (map: L.Map) => {
      mapRef.current = map;
    };
    
    return (
      <div className="w-full h-full rounded-lg overflow-hidden relative z-0">
        <MapContainer 
          center={[centerLat || 28.6139, centerLng || 77.2090]} 
          zoom={5} 
          style={{ height: '100%', width: '100%' }}
          className="rounded-lg"
          zoomControl={false}
          scrollWheelZoom={true}
          minZoom={3}
          maxZoom={18}
          worldCopyJump={true}
          whenReady={() => {}}
          ref={handleMapLoad}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            zIndex={0}
          />
          {data && data.length > 0 ? (
            data.map((point, index) => {
              const radius = 2000;
              const color = getCategoryColor(point.category);
              const borderColor = color;
              
              console.log(`Processing point ${index}: lat=${point.lat}, lng=${point.lng}, count=${point.count}`);
              
              if (!point.lat || !point.lng || isNaN(point.lat) || isNaN(point.lng)) {
                console.error(`Invalid coordinates for point ${index}:`, point);
                return null;
              }
              
              return (
                <Circle
                  key={`${point.lat}-${point.lng}-${index}`}
                  center={[point.lat, point.lng]}
                  radius={radius}
                  fillColor={color}
                  color={borderColor}
                  fillOpacity={0.85}
                  weight={2}
                >
                  <Popup className="z-[10000]" maxWidth={300}>
                    <div className="min-w-[150px]">
                      <div className="font-bold text-base text-gray-800 mb-2 pb-2 border-b border-gray-200">
                        {t(`categories.${point.category}`) || point.category}
                      </div>
                      <div className="flex items-center mb-2">
                        <span className="font-semibold text-gray-700 mr-2 text-sm">Complaints:</span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          {point.count}
                        </span>
                      </div>
                      {point.description && (
                        <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                          <p className="text-gray-700 text-xs">{point.description}</p>
                        </div>
                      )}
                      <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
                        <span className="font-medium">Location:</span> {point.lat.toFixed(4)}, {point.lng.toFixed(4)}
                      </div>
                    </div>
                  </Popup>
                </Circle>
              );
            })
          ) : (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 rounded-lg shadow-lg z-[10000]">
              <p className="text-gray-600">No location data available</p>
            </div>
          )}
        </MapContainer>
        
        {/* Custom map controls for mobile */}
        <div className="absolute top-4 right-4 z-10 flex flex-col space-y-2 sm:hidden">
          <button 
            onClick={handleZoomIn}
            className="bg-white p-3 rounded-lg shadow-md hover:bg-gray-50 active:bg-gray-100 transition-colors"
            aria-label="Zoom in"
          >
            <ZoomIn className="h-5 w-5 text-gray-600" />
          </button>
          <button 
            onClick={handleZoomOut}
            className="bg-white p-3 rounded-lg shadow-md hover:bg-gray-50 active:bg-gray-100 transition-colors"
            aria-label="Zoom out"
          >
            <ZoomOut className="h-5 w-5 text-gray-600" />
          </button>
          <button 
            onClick={handleResetView}
            className="bg-white p-3 rounded-lg shadow-md hover:bg-gray-50 active:bg-gray-100 transition-colors"
            aria-label="Reset view"
          >
            <RotateCcw className="h-5 w-5 text-gray-600" />
          </button>
        </div>
        
        <div className="absolute bottom-4 right-4 bg-white p-3 rounded-lg shadow-lg z-10 hidden sm:block">
          <div className="text-xs font-medium text-gray-700 mb-2">{t('analyticsPage.categories')}</div>
          <div className="space-y-1">
            {['roads', 'garbage', 'streetlight', 'water', 'electricity'].map(category => (
              <div key={category} className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: getCategoryColor(category) }}
                />
                <span className="text-xs text-gray-600 capitalize">
                  {t(`categories.${category}`) || category}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center space-x-3">
              <img src="/hero.png" alt="Logo" className="w-8 h-8" />
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-800 truncate">{t('mapView.title')}</h1>
                <p className="text-xs text-gray-600 hidden sm:block">{t('mapView.subtitle')}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* Desktop controls */}
            <div className="hidden sm:flex items-center space-x-2">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">{t('mapView.allCategories')}</option>
                <option value="roads">{t('categories.roads')}</option>
                <option value="garbage">{t('categories.garbage')}</option>
                <option value="streetlight">{t('categories.streetlight')}</option>
                <option value="water">{t('categories.water')}</option>
                <option value="electricity">{t('categories.electricity')}</option>
              </select>
              <select
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value as any)}
                className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="7d">{t('analyticsPage.last7Days')}</option>
                <option value="30d">{t('analyticsPage.last30Days')}</option>
                <option value="90d">{t('analyticsPage.last90Days')}</option>
                <option value="1y">{t('analyticsPage.lastYear')}</option>
              </select>
              <button 
                onClick={exportMapData}
                className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-sm"
              >
                <Download className="w-4 h-4" />
                <span>{t('mapView.export')}</span>
              </button>
              <button
                onClick={handleSignOut}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('auth.logout')}
              </button>
            </div>
            
            {/* Mobile controls */}
            <div className="sm:hidden flex items-center space-x-2">
              <button 
                onClick={exportMapData}
                className="flex items-center space-x-1 px-2 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-xs"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={handleSignOut}
                className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </div>
        
        {/* Additional controls for mobile - moved below header on small screens */}
        <div className="flex items-center justify-between mt-2 sm:mt-3 space-x-2 sm:hidden">
          <div className="flex-1 min-w-0">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('mapView.allCategories')}</option>
              <option value="roads">{t('categories.roads')}</option>
              <option value="garbage">{t('categories.garbage')}</option>
              <option value="streetlight">{t('categories.streetlight')}</option>
              <option value="water">{t('categories.water')}</option>
              <option value="electricity">{t('categories.electricity')}</option>
            </select>
          </div>
          <div className="flex space-x-2">
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value as any)}
              className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7d">7D</option>
              <option value="30d">30D</option>
              <option value="90d">90D</option>
              <option value="1y">1Y</option>
            </select>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <ResponsiveSidebar 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          userRole="official"
          currentPage="map-view"
        />

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[9998]"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-hidden">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">{t('mapView.geographicDistribution')}</h3>
              <Map className="w-5 h-5 text-gray-400" />
            </div>
            <div className="flex-1 min-h-0">
              <div className="w-full h-full">
                <GeographicMap data={locationData} />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MapView;